import React, { useMemo, useState } from 'react';
import { PageContainer } from '@/components/ui/PageContainer';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { SeaVentService, type SeaVentVenue } from '@/services/SeaVentService';

export function SeaVentSearch() {
  const svc = useMemo(() => new SeaVentService(), []);
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SeaVentVenue[] | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResults(null);
    if (!name.trim() || !city.trim()) {
      setError('Please enter both venue name and city');
      return;
    }
    setLoading(true);
    try {
      const data = await svc.search(name, city, country);
      setResults(data);
    } catch (e: any) {
      setError(e?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="SeaVent Prototype Search"
        description="Read-only Cvent venue search by name and city"
      />

      <Card>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Venue name (e.g., Marriott)"
                aria-label="Venue name"
              />
            </div>
            <div>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City (e.g., Chicago)"
                aria-label="City"
              />
            </div>
            <div>
              <Input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Country (optional)"
                aria-label="Country"
              />
            </div>
            <div className="md:col-span-4 flex justify-end">
              <Button type="submit" disabled={loading}>
                {loading ? <div className="flex items-center gap-2"><Spinner size="sm" /> Searching...</div> : 'Search'}
              </Button>
            </div>
          </form>
          {error && (
            <div className="mt-3 text-sm text-error-600" role="alert">{error}</div>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 space-y-3">
        {loading && (
          <div className="bg-background rounded-lg p-4 border">
            <div className="flex items-center gap-3 text-muted-foreground"><Spinner size="sm" /> Fetching results from Cvent…</div>
          </div>
        )}
        {!loading && results && (
          results.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {results.map((v, i) => (
                <div key={`${v.cvent_venue_id || v.name || 'row'}-${i}`} className="bg-background rounded-lg p-4 border">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-foreground">{v.name || '—'}</div>
                      <div className="text-sm text-muted-foreground">{[v.address, v.city, v.state_province, v.country].filter(Boolean).join(', ')}</div>
                    </div>
                    <div className="text-xs text-muted-foreground text-right">
                      <div>Venue ID: <span className="text-foreground">{v.cvent_venue_id || '—'}</span></div>
                      <div>Venue Code: <span className="text-foreground">{v.cvent_venue_code || '—'}</span></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-background rounded-lg p-6 border text-muted-foreground">No results found.</div>
          )
        )}
      </div>
    </PageContainer>
  );
}









